import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { FunctionComponent } from 'react';
import { forTemplate } from '@trello/i18n';
import { Tooltip } from '@trello/nachos/tooltip';
import { useBoardJoinButtonAddMemberMutation } from './BoardJoinButtonAddMemberMutation.generated';
import { memberId } from '@trello/session-cookie';
import { Analytics, getScreenFromUrl } from '@trello/atlassian-analytics';
import { isMemberAdmin, canJoin } from '@trello/boards';
import { useBoardJoinButtonQuery } from './BoardJoinButtonQuery.generated';
import { DynamicButton } from '@trello/dynamic-tokens';

const format = forTemplate('board_header');

interface BoardJoinButtonProps {
  boardId: string;
  workspaceId?: string;
  enterpriseId?: string;
}

export const BoardJoinButton: FunctionComponent<BoardJoinButtonProps> = ({
  boardId,
  workspaceId,
  enterpriseId,
}) => {
  const { data } = useBoardJoinButtonQuery({
    skip: !memberId,
    variables: {
      boardId,
      memberId: memberId ?? '',
    },
    fetchPolicy: 'cache-only',
    waitOn: ['CurrentBoardFull'],
  });

  // Used to optimistically hide button between click and when the query data is updated.
  const [hideButton, setHideButton] = useState(false);
  useEffect(() => {
    setHideButton(false);
  }, [data]);

  const [addMember] = useBoardJoinButtonAddMemberMutation();

  const memberType = useMemo(
    () =>
      data?.member && data.board && isMemberAdmin(data.member, data.board)
        ? 'admin'
        : 'normal',
    [data?.board, data?.member],
  );

  const showButton = useMemo(
    () =>
      !hideButton &&
      data?.member &&
      data.board &&
      data.board.organization &&
      canJoin(
        data?.board?.organization,
        data.member,
        data.board,
        data.board.organization.enterprise ?? undefined,
      ),
    [hideButton, data?.board, data?.member],
  );

  const onClick = useCallback(async () => {
    if (!memberId) return;

    const traceId = Analytics.startTask({
      taskName: 'edit-board/members/join',
      source: 'boardScreen',
    });

    setHideButton(true);

    try {
      await addMember({
        variables: {
          boardId,
          member: { id: memberId },
          type: memberType,
          acceptUnconfirmed: true,
          allowBillableGuest: true,
          traceId,
        },
      });

      Analytics.sendTrackEvent({
        action: 'added',
        actionSubject: 'member',
        actionSubjectId: memberId,
        source: getScreenFromUrl(),
        containers: {
          board: {
            id: boardId,
          },
          organization: {
            id: workspaceId,
          },
          workspace: {
            id: workspaceId,
          },
        },
        attributes: {
          addedTo: 'board',
          confirmed: data?.member?.confirmed,
          memberType,
          taskId: traceId,
          // add prop for Trello Invite From Slack project
          // source: member.source,
        },
      });
      Analytics.taskSucceeded({
        taskName: 'edit-board/members/join',
        source: 'boardScreen',
        traceId,
      });
    } catch (e) {
      Analytics.taskFailed({
        taskName: 'edit-board/members/join',
        source: 'boardScreen',
        traceId,
        // @ts-expect-error
        error: e,
      });
    }
  }, [addMember, boardId, data?.member?.confirmed, memberType, workspaceId]);

  return showButton ? (
    <Tooltip content={format('workspace-members-can-join-this-board')}>
      <DynamicButton onClick={onClick} aria-label={format('join-board')}>
        {format('join-board')}
      </DynamicButton>
    </Tooltip>
  ) : null;
};
